import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SetPointVarianceTimeframeEnum } from "../../common/enums/SetpointVarianceTimeframeEnum";
import { SetPointVarianceTypeEnum } from "../../common/enums/SetpointVarianceTypeEnum";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { IKeyValuePair } from "../../common/models/KeyValuePair";
import i18n from "../../services/localization-service/i18n";
import { IEditDTO, IEditNotificationListDTO, IEditRecipientDTO, IEditTagDTO, /*IEditTagSearchResultDeviceDTO,*/ IReferenceDTO, IReferenceNotificationListDTO, SetPointPrecedenceTypeEnum } from "../../services/setpoint-administration-service/Models";
import { ActionMenuItem, BaseActions } from "../action-menu/ActionMenuItem";
import { faList, faListCheck } from "@elynx/free-solid-svg-icons";
import { RecipientTypeEnum } from "../../common/enums/RecipientTypeEnum";
import _ from "lodash";
import { SetPointTypeEnum } from "../../common/enums/SetpointTypeEnum";
import { FieldNamesMarkedBoolean } from "react-hook-form";
import { IFormRecipient, IFormUserRecipient, RecipientsFormType } from "../form-recipient-list/FormRecipientListUtils";
import { NotificationListsFormType } from "../form-notification-lists/FormNotificationListsUtils";
import AlarmSchemeListUtils, { AlarmSchemesFormType } from "../alarm-scheme-list/AlarmSchemeListUtils";
// import Enumerable from "linq";
import { DataTypeEnum } from "../../common/enums/DataTypeEnum";
import { DateRangeType } from "../../common/enums/DRCXEnums";

export enum SetPointAdminMode {
    Add = 0,
    Edit = 1
}

export enum StepEnum {
    Logic = 1,
    Usage = 2,
    Notifications = 3,
    Actions = 4,
    Summary = 5
}

export type SetpointFormType = {
    unitClassification: number;

    expanded: boolean;

    // devices: IEditTagSearchResultDeviceDTO[];
    // wells: IEditTagSearchResultDeviceDTO[];
    // tanks: IEditTagSearchResultDeviceDTO[];
    // tankBatteries: IEditTagSearchResultDeviceDTO[];
    deviceTags: IEditTagDTO[];
    wellTags: IEditTagDTO[];
    tankTags: IEditTagDTO[];
    tankBatteryTags: IEditTagDTO[];

    originalTagCount: number;

} & RecipientsFormType &
    NotificationListsFormType & 
    AlarmSchemesFormType &
    Omit<IEditDTO, 'notificationLists' | 'alarmSchemes' | 'tags'>;


export const operators: IKeyValuePair<string, string>[] = [
    {Key: "greater than", Value: '>'},
    {Key: "less than", Value: '<'},
    {Key: "not equal to", Value: '!='},
    {Key: "equal to", Value: '='}
];

export const textOperators: IKeyValuePair<string, string>[] = [
    {Key: "contains", Value: 'CONTAINS'},
    {Key: "does not contain", Value: 'DOES NOT CONTAIN'},
    {Key: "exactly matches", Value: 'EXACTLY MATCHES'},
    {Key: "does not exactly match", Value: 'DOES NOT EXACTLY MATCH'},
    {Key: "begins with", Value: 'BEGINS WITH'},
    {Key: "ends with", Value: 'ENDS WITH'}
];

export const varianceOperators: IKeyValuePair<string, SetPointVarianceTypeEnum>[] = [
    {Key: i18n.t("setpointAdmin.varianceType.either"), Value: SetPointVarianceTypeEnum.Either},
    {Key: i18n.t("setpointAdmin.varianceType.increase"), Value: SetPointVarianceTypeEnum.Increase},
    {Key: i18n.t("setpointAdmin.varianceType.decrease"), Value: SetPointVarianceTypeEnum.Decrease}
];

export const varianceTimeframes: IKeyValuePair<string, SetPointVarianceTimeframeEnum>[] = [
    {Key: i18n.t("setpointAdmin.varianceTimeframe.hours"), Value: SetPointVarianceTimeframeEnum.Hours},
    {Key: i18n.t("setpointAdmin.varianceTimeframe.days"), Value: SetPointVarianceTimeframeEnum.Days},
    {Key: i18n.t("setpointAdmin.varianceTimeframe.months"), Value: SetPointVarianceTimeframeEnum.Months}
];

export const timeComparisonTimeframes: IKeyValuePair<string, DateRangeType>[] = [
    {Key: i18n.t("setpointAdmin.timeComparisonTimeSpan.minutes"), Value: DateRangeType.Minute},
    {Key: i18n.t("setpointAdmin.timeComparisonTimeSpan.hours"), Value: DateRangeType.Hour},
    {Key: i18n.t("setpointAdmin.timeComparisonTimeSpan.days"), Value: DateRangeType.Day}
];

export const timeOperators: IKeyValuePair<string, string>[] = [
    {Key: "older than", Value: "OLDER THAN"}
];  

export enum SetpointUsageActions  {
    SelectAll = 'selectall',
    RemoveAll = 'removeall'
}

export type SetpointUsageActionItem = {
    action: SetpointUsageActions | BaseActions | string
} & ActionMenuItem;

export const usageActionItems: SetpointUsageActionItem[] = [{
    label: i18n.t("buttons.selectAllVisible"),
    icon: () => <FontAwesomeIcon icon={faListCheck} size="xs" />,
    action: SetpointUsageActions.SelectAll,
    order: 0
},{
    label: i18n.t("buttons.clearAllVisible"),
    icon: () => <FontAwesomeIcon icon={faList} size="xs" />,
    action: SetpointUsageActions.RemoveAll,
    order: 1
}];

export type SetpointMessageTemplates = {
    emailAlarmMessageSubject: string;
    emailAlarmMessageBody: string;
    emailClearMessageSubject: string;
    emailClearMessageBody: string;
    voiceAlarmMessageBody: string;
    voiceClearMessageBody: string
}

const _getMessageTemplates = (model: IEditDTO | SetpointFormType,
    referenceData: IReferenceDTO): SetpointMessageTemplates => {

    const isAnalogDefault = !model.isDiscrete && model.setPointType === SetPointPrecedenceTypeEnum.Database && 
        model.comparisonType === SetPointTypeEnum.ValueComparison;

    const isDiscreteDefault = (model.isDiscrete || model.setPointType === SetPointPrecedenceTypeEnum.Field) && 
        model.comparisonType === SetPointTypeEnum.ValueComparison;

    const isVarianceDefault = model.setPointType === SetPointPrecedenceTypeEnum.Database && 
        (model.comparisonType === SetPointTypeEnum.VariationAmount ||
            model.comparisonType === SetPointTypeEnum.VariationPercent);

    const isTimeComparisonDefault = model.setPointType === SetPointPrecedenceTypeEnum.Database && 
        model.comparisonType === SetPointTypeEnum.TimeComparison;

    const emailAlarmMessageSubject = referenceData.messages.find(o => o.isAlarmOnly &&
        o.isSubject &&
        o.dataType === model.dataType &&
        o.isAnalogDefault === (isAnalogDefault || isVarianceDefault) &&
        o.isDiscreteDefault === model.isDiscrete &&
        o.isTimeComparisonDefault === isTimeComparisonDefault &&
        o.objectType === eLynxObjectTypeEnum.None)?.template ?? '';

    const emailAlarmMessageBody = referenceData.messages.find(o => o.isAlarmOnly &&
        o.isBody &&
        o.dataType === model.dataType &&
        o.isAnalogDefault === isAnalogDefault &&
        o.isDiscreteDefault === isDiscreteDefault &&
        o.isVarianceDefault === isVarianceDefault &&
        o.isTimeComparisonDefault === isTimeComparisonDefault &&
        o.objectType === eLynxObjectTypeEnum.None)?.template ?? '';

    const emailClearMessageSubject = referenceData.messages.find(o => o.isClearOnly &&
        o.isSubject &&
        o.dataType === model.dataType &&
        o.isAnalogDefault === (isAnalogDefault || isVarianceDefault) &&
        o.isDiscreteDefault === model.isDiscrete &&
        o.isTimeComparisonDefault === isTimeComparisonDefault &&
        o.objectType === eLynxObjectTypeEnum.None)?.template ?? '';

    const emailClearMessageBody = referenceData.messages.find(o => o.isClearOnly &&
        o.isBody &&
        o.dataType === model.dataType &&
        o.isAnalogDefault === isAnalogDefault &&
        o.isDiscreteDefault === isDiscreteDefault &&
        o.isVarianceDefault === isVarianceDefault &&
        o.isTimeComparisonDefault === isTimeComparisonDefault &&
        o.objectType === eLynxObjectTypeEnum.None)?.template ?? '';

    return {
        emailAlarmMessageSubject,
        emailAlarmMessageBody,
        emailClearMessageSubject,
        emailClearMessageBody,
        voiceAlarmMessageBody: '',
        voiceClearMessageBody: ''
    };
}

const _updateNotificationProperties = (model: IEditDTO | SetpointFormType, formValues: SetpointFormType) => {
    const tags = [
        ...formValues.deviceTags,
        ...formValues.wellTags,
        ...formValues.tankTags,
        ...formValues.tankBatteryTags
    ];

    if (tags.length > 0) {

        // Retrieve the count of "Notify on Alarm" for ALL tags linked to the Alarm Setpoint
        // that are TRUE; used in determining the Alarm Setpoints' "Notify on Alarm" properties
        const tagsWithNotifyOnAlarm = tags.filter(o => o.notifyOnAlarm);

        // "Notify On Alarm" for the Alarm Setpoint is TRUE when "Notify on Alarm" for any
        // of the tags linked to the Alarm Setpoint are TRUE; otherwise FALSE.
        formValues.notifyOnAlarm = (tagsWithNotifyOnAlarm.length > 0);

        // "Notify On Alarm" for the Alarm Setpoint is INDETERMINATE when "Notify on Alarm"
        // for some of the tags linked to the Alarm Setpoint are TRUE and some are FALSE
        // this.notifyOnAlarmIsIndeterminate = (
        //     !this._singleTagMode &&
        //     (tagsWithNotifyOnAlarm > 0) &&
        //     (tagsWithNotifyOnAlarm !== this._tags.length));

        // "Notify on Normal" can be modified only when "Notify on Alarm"
        // is TRUE or INDETERMINATE
        if (formValues.notifyOnAlarm /*|| this.notifyOnAlarmIsIndeterminate*/) {

            // Retrieve the count of "Notify on Normal" for ALL tags linked to the
            // Alarm Setpoint that are TRUE; used in determining the Alarm Setpoints'
            // "Notify on Alarm" properties
            const tagsWithNotifyOnNormal = tags.filter(o => o.notifyOnNormal);

            // "Notify On Normal" for the Alarm Setpoint is TRUE when "Notify on Normal"
            // for any of the tags linked to the Alarm Setpoint are TRUE; otherwise FALSE.
            formValues.notifyOnNormal = (tagsWithNotifyOnNormal.length > 0);

            // "Notify On Normal" for the Alarm Setpoint is INDETERMINATE when
            // "Notify on Normal" for some of the tags linked to the Alarm Setpoint are TRUE
            // and some are FALSE
            // this.notifyOnNormalIsIndeterminate = (
            //     !this._singleTagMode &&
            //     (tagsWithNotifyOnNormal > 0) &&
            //     (tagsWithNotifyOnNormal !== this._tags.length));

        } else {
            formValues.notifyOnNormal = false;
            //this.notifyOnNormalIsIndeterminate = false;
        }
    } else {

        // The INDETERMINATE state is NOT possible without linked tags
        //this.notifyOnAlarmIsIndeterminate = false;
        //this.notifyOnNormalIsIndeterminate = false;

        // "Notify on Normal" is always FALSE when "Notify on Alarm" is FALSE
        if (!formValues.notifyOnAlarm) {
            formValues.notifyOnNormal = false;
        }
    }
}

const AlarmSetpointEditorUtils = {
    mapToModel: (setpointFormData: SetpointFormType, 
                        evaluationChanges: boolean,
                        //evaluationTagChanges: boolean
                    ): IEditDTO => {

        const model: IEditDTO = {
            organizationID: setpointFormData.organizationID,
            setPointID: setpointFormData.setPointID,
            setPointName: setpointFormData.setPointName,
            setPointType: setpointFormData.setPointType,
            isDiscrete: setpointFormData.setPointType === SetPointPrecedenceTypeEnum.Field || setpointFormData.isDiscrete,
            comparisonType: setpointFormData.comparisonType,
            operator: setpointFormData.operator,
            value: setpointFormData.value,
            unitOfMeasureID: setpointFormData.unitOfMeasureID,
            varianceType: setpointFormData.varianceType,
            varianceWindow: setpointFormData.varianceWindow,
            varianceTimeframeType: setpointFormData.varianceTimeframeType,
            emailAlarmMessageSubject: setpointFormData.emailAlarmMessageSubject,
            emailAlarmMessageBody: setpointFormData.emailAlarmMessageBody,
            voiceAlarmMessageBody: setpointFormData.voiceAlarmMessageBody,
            emailClearMessageSubject: setpointFormData.emailClearMessageSubject,
            emailClearMessageBody: setpointFormData.emailClearMessageBody,
            voiceClearMessageBody: setpointFormData.voiceClearMessageBody,
            alarmColor: setpointFormData.alarmColor,
            acknowledgeColor: setpointFormData.acknowledgeColor,
            notifyOnAlarm: setpointFormData.notifyOnAlarm,
            notifyOnNormal: setpointFormData.notifyOnNormal,
            suppressAlarmAlarm: setpointFormData.suppressAlarmAlarm,
            suppressNormalNormal: setpointFormData.suppressNormalNormal,
            alarmSchemes: [],
            notificationLists: [],
            recipients: [],
            tags: [],
            hasEvaluationPropertyChanges: evaluationChanges, 
            tagLevelEdit: false,
            textValue: setpointFormData.textValue,
            dataType: setpointFormData.dataType,
            customMessaging: setpointFormData.customMessaging,
            timespanType: setpointFormData.timespanType
        };

        const alarmScheme = setpointFormData.alarmSchemes.find(o => o.selected);
        if (alarmScheme) {
            model.alarmSchemeID = alarmScheme.id;
        } else {
            let recipients: IEditRecipientDTO[] = [];
            setpointFormData.userRecipients.filter(o => o.recipients.find(v => v.selected))
                    .forEach(o => {
                        recipients = recipients.concat(o.recipients.filter(v => v.selected)
                                    .map(v => {
                                        const item: IEditRecipientDTO = {
                                            organizationID: v.organizationID,
                                            userID: o.userID,
                                            recipientID: v.id
                                        };

                                        return item;
                                    }))
                    });

                    //sort by organizationID, then by userID, then by recipientID
                    model.recipients = recipients.sort((a, b) => a.organizationID - b.organizationID || a.userID - b.userID || a.recipientID - b.recipientID);


            const notificationLists = setpointFormData.notificationLists.filter(o => o.selected).map(o => {
                const item: IEditNotificationListDTO = {
                    organizationID: o.organizationID,
                    notificationListID: o.id
                };

                return item;
            });

            model.notificationLists = notificationLists.sort((a, b) => a.organizationID - b.organizationID || a.notificationListID - b.notificationListID);
        }
        
        // let tags: IEditTagDTO[] = [];

        // setpointFormData.devices.filter(o => o.tags.find(v => v.selected))
        //                 .forEach(o => {

        //                     tags = tags.concat(o.tags.filter(v => v.selected)
        //                     .map(v => {
        //                         const item: IEditTagDTO = {

        //                             tagObjectID: o.id, 
        //                             tagObjectName: o.name,
        //                             tagObjectTypeID: o.objectTypeID,
        //                             tagID: v.id,
        //                             tagName: v.name,
        //                             tatID: 0,
        //                             tagHidden: false,
        //                             tagDisabled: false,
        //                             notifyOnAlarm: setpointFormData.notifyOnAlarm,
        //                             notifyOnNormal: setpointFormData.notifyOnNormal,
        //                             tagUnitOfMeasureID: 0,
        //                             tagUomIsIncompatible: false
        //                         };

        //                         return item;
        //                     }));
        //                 });

        // setpointFormData.tankBatteries.filter(o => o.tags.find(v => v.selected))
        // .forEach(o => {

        //     tags = tags.concat(o.tags.filter(v => v.selected)
        //     .map(v => {
        //         const item: IEditTagDTO = {

        //             tagObjectID: o.id, 
        //             tagObjectName: o.name,
        //             tagObjectTypeID: o.objectTypeID,
        //             tagID: v.id,
        //             tagName: v.name,
        //             tatID: 0,
        //             tagHidden: false,
        //             tagDisabled: false,
        //             notifyOnAlarm: setpointFormData.notifyOnAlarm,
        //             notifyOnNormal: setpointFormData.notifyOnNormal,
        //             tagUnitOfMeasureID: 0,
        //             tagUomIsIncompatible: false
        //         };

        //         return item;
        //     }));
        // });

        // setpointFormData.tanks.filter(o => o.tags.find(v => v.selected))
        // .forEach(o => {

        //     tags = tags.concat(o.tags.filter(v => v.selected)
        //     .map(v => {
        //         const item: IEditTagDTO = {

        //             tagObjectID: o.id, 
        //             tagObjectName: o.name,
        //             tagObjectTypeID: o.objectTypeID,
        //             tagID: v.id,
        //             tagName: v.name,
        //             tatID: 0,
        //             tagHidden: false,
        //             tagDisabled: false,
        //             notifyOnAlarm: setpointFormData.notifyOnAlarm,
        //             notifyOnNormal: setpointFormData.notifyOnNormal,
        //             tagUnitOfMeasureID: 0,
        //             tagUomIsIncompatible: false
        //         };

        //         return item;
        //     }));
        // });

        // setpointFormData.wells.filter(o => o.tags.find(v => v.selected))
        // .forEach(o => {

        //     tags = tags.concat(o.tags.filter(v => v.selected)
        //     .map(v => {
        //         const item: IEditTagDTO = {

        //             tagObjectID: o.id, 
        //             tagObjectName: o.name,
        //             tagObjectTypeID: o.objectTypeID,
        //             tagID: v.id,
        //             tagName: v.name,
        //             tatID: 0,
        //             tagHidden: false,
        //             tagDisabled: false,
        //             notifyOnAlarm: setpointFormData.notifyOnAlarm,
        //             notifyOnNormal: setpointFormData.notifyOnNormal,
        //             tagUnitOfMeasureID: 0,
        //             tagUomIsIncompatible: false
        //         };

        //         return item;
        //     }));
        // });

        // model.tags = setpointFormData.tags.sort((a, b) => a.tagObjectID - b.tagObjectID || a.tagID - b.tagID);
        model.tags = [
            ...setpointFormData.deviceTags.map(t => ({
                 ...t,
                 notifyOnAlarm: setpointFormData.notifyOnAlarm,
                 notifyOnNormal: setpointFormData.notifyOnNormal
            })),
            ...setpointFormData.wellTags.map(t => ({
                 ...t,
                 notifyOnAlarm: setpointFormData.notifyOnAlarm,
                 notifyOnNormal: setpointFormData.notifyOnNormal
            })),
            ...setpointFormData.tankTags.map(t => ({
                 ...t,
                 notifyOnAlarm: setpointFormData.notifyOnAlarm,
                 notifyOnNormal: setpointFormData.notifyOnNormal
            })),
            ...setpointFormData.tankBatteryTags.map(t => ({
                 ...t,
                 notifyOnAlarm: setpointFormData.notifyOnAlarm,
                 notifyOnNormal: setpointFormData.notifyOnNormal
            }))
        ].sort((a, b) => a.tagObjectID - b.tagObjectID || a.tagID - b.tagID);

        return model;
    },
    mapToFormValues: (model: IEditDTO,// | SetpointFormType, 
        unitClassification: number, 
        //availableObjects: IEditTagSearchResultDeviceDTO[], 
        referenceData: IReferenceDTO,
        allowVoiceAlarm: boolean,
        dataType: DataTypeEnum,
        copy: boolean | undefined,
        tagID: number | undefined,
        uomID: number | undefined,
        keepMessaging?: boolean): SetpointFormType  => {


        const formValues: SetpointFormType = {...model,
            dataType: dataType,
            operator: model.setPointID <= 0 && dataType === DataTypeEnum.String ? textOperators[0].Value :  model.operator,
            unitClassification: uomID !== undefined ? 
                    referenceData.unitsOfMeasure.find(o => o.id === uomID)?.unitClassificationID ?? -1 :
                model.setPointID > 0 ? 
                    referenceData.unitsOfMeasure.find(o => o.id === model.unitOfMeasureID)?.unitClassificationID ?? -1 : 
                    unitClassification,
            expanded: false,
            // devices: availableObjects.filter(o => o.objectTypeID === eLynxObjectTypeEnum.Device),
            // wells: availableObjects.filter(o => o.objectTypeID === eLynxObjectTypeEnum.Well),
            // tanks: availableObjects.filter(o => o.objectTypeID === eLynxObjectTypeEnum.Tank),
            // tankBatteries: availableObjects.filter(o => o.objectTypeID === eLynxObjectTypeEnum.TankBattery),
            deviceTags: model.tags
                .filter(o => o.tagObjectTypeID === eLynxObjectTypeEnum.Device)
                .map(t => ({
                    ...t,
                    selected: true
                })),
            wellTags: model.tags
                .filter(o => o.tagObjectTypeID === eLynxObjectTypeEnum.Well)
                .map(t => ({
                     ...t,
                      selected: true
                     })),
            tankTags: model.tags
                .filter(o => o.tagObjectTypeID === eLynxObjectTypeEnum.Tank)
                .map(t => ({
                    ...t,
                    selected: true
                })),
            tankBatteryTags: model.tags
                .filter(o => o.tagObjectTypeID === eLynxObjectTypeEnum.TankBattery)
                .map(t => ({
                    ...t,
                    selected: true
                })),
            userRecipients: [],
            notificationLists: _.cloneDeep(referenceData.notificationLists.filter(o => o.organizationID === model.organizationID || 
                                            model.originalNotificationLists?.find(v=> v.notificationListID === o.id) !== undefined)
                                            .filter((value, index, self) => self.findIndex(x => x.id === value.id) === index)
                                           .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))) as IReferenceNotificationListDTO[],
            alarmSchemes: _.cloneDeep(referenceData.alarmSchemes).map(o => {
                return {...o,
                    setpoints: []
                };
            }), 
            originalTagCount: model.tags.length,
            customMessaging: model.customMessaging,
            timespanType: model.timespanType
        };

        if (uomID !== undefined) {

            formValues.unitOfMeasureID = uomID;
        }

        if (model.isDiscrete) {
            formValues.setPointType = SetPointPrecedenceTypeEnum.Field;
        } else if (model.setPointType === undefined) {
            formValues.setPointType = SetPointPrecedenceTypeEnum.Database;
        }

        // formValues.devices.forEach(o => o.allowSelection = true);
        // formValues.wells.forEach(o => o.allowSelection = true);
        // formValues.tanks.forEach(o => o.allowSelection = true);
        // formValues.tankBatteries.forEach(o => o.allowSelection = true);


        // formValues.devices.flatMap(o => o.tags).forEach(o => o.selected = false);
        // formValues.wells.flatMap(o => o.tags).forEach(o => o.selected = false);
        // formValues.tanks.flatMap(o => o.tags).forEach(o => o.selected = false);
        // formValues.tankBatteries.flatMap(o => o.tags).forEach(o => o.selected = false);


        // const groupedTags = Enumerable.from(model.tags)
        //                         .groupBy(o => `${o.tagObjectID}:${o.tagObjectTypeID}:${o.tagObjectName}`);

        // groupedTags.forEach(o => {
        //     const key = o.key().split(':');
        //     const objectID = +(key[0]);
        //     const objectType = +(key[1]);
        //     const name = key[2];

        //     const items = o.toArray();

        //     if (!availableObjects.find(v => v.id === objectID && v.objectTypeID === objectType)) {

        //         const item: IEditTagSearchResultDeviceDTO = {
        //             id: objectID,
        //             objectTypeID: objectType,
        //             name: name,
        //             allowSelection: false,
        //             tags: items.map(v => {
        //                 return {
        //                     id: v.tagID,
        //                     name: v.tagName,
        //                     selected: true
        //                 }
        //             })
        //         };

        //         switch(objectType) {
        //             case eLynxObjectTypeEnum.Device:
        //                 formValues.devices.push(item);
        //                 break;

        //             case eLynxObjectTypeEnum.Well:
        //                 formValues.wells.push(item);
        //                 break;

        //             case eLynxObjectTypeEnum.Tank:
        //                 formValues.tanks.push(item);
        //                 break;

        //             case eLynxObjectTypeEnum.TankBattery:
        //                 formValues.tankBatteries.push(item);
        //                 break;
        //         }
        //     }
        // });

        if (copy) {

            formValues.setPointID = 0;
            formValues.setPointName = `${formValues.setPointName.slice(0, 40)} - [COPY]`;
            formValues.notifyOnAlarm = true;
            formValues.notifyOnNormal = true;
            //formValues.tags = [];`
            formValues.deviceTags = [];
            formValues.wellTags = [];
            formValues.tankTags = [];
            formValues.tankBatteryTags = [];
        }


        // model.tags.forEach(o => {

        //     switch(o.tagObjectTypeID) {
        //         case eLynxObjectTypeEnum.Device: {
        //             const item = formValues.devices.find(v => v.id === o.tagObjectID && v.objectTypeID === o.tagObjectTypeID)?.tags.find(v => v.id === o.tagID);
        //             if (item) {
        //                 item.selected = true;
        //             }
        //         }
        //             break;

        //         case eLynxObjectTypeEnum.Well: {
        //                 const item = formValues.wells.find(v => v.id === o.tagObjectID && v.objectTypeID === o.tagObjectTypeID)?.tags.find(v => v.id === o.tagID);
        //                 if (item) {
        //                     item.selected = true;
        //                 }
        //             }                    
        //             break;

        //         case eLynxObjectTypeEnum.Tank: {
        //                 const item = formValues.tanks.find(v => v.id === o.tagObjectID && v.objectTypeID === o.tagObjectTypeID)?.tags.find(v => v.id === o.tagID);
        //                 if (item) {
        //                     item.selected = true;
        //                 }
        //             }
                
        //             break;

        //         case eLynxObjectTypeEnum.TankBattery: {
        //             const item = formValues.tankBatteries.find(v => v.id === o.tagObjectID && v.objectTypeID === o.tagObjectTypeID)?.tags.find(v => v.id === o.tagID);
        //             if (item) {
        //                 item.selected = true;
        //             }
        //         }
        //             break;

        //         default: 
        //             break;
        //     }

        // });

        // if (model.setPointID <= 0 && tagID !== undefined) {
        //     const item = formValues.devices.flatMap(o => o.tags)
        //                     .concat(formValues.wells.flatMap(o => o.tags))
        //                     .concat(formValues.tanks.flatMap(o => o.tags))
        //                     .concat(formValues.tankBatteries.flatMap(o => o.tags))            
        //                 .find(v => v.id === tagID);

        //     if (item) {
        //         item.selected = true;
        //     }
        // }
        

        formValues.notificationLists.forEach(o => o.selected === false);

        model.originalNotificationLists?.forEach(o => {

            const dto = o as IEditNotificationListDTO;

            const item = formValues.notificationLists.find(v => v.id === dto.notificationListID);
            if (item) {
                item.selected = true;
            }

        });

        formValues.alarmSchemes.forEach(o => {

            AlarmSchemeListUtils.updateAlarmSchemeForm(o, referenceData);
            o.selected = model.alarmSchemeID !== undefined && o.id === model.alarmSchemeID;
        });

        // include disabled recipients when editing a setpoint
        referenceData.users.filter(o => o.id > 0 &&  ((!o.isDisabled && o.id > 0) || model.setPointID > 0))
            .forEach(o => {
            const userRecipient: IFormUserRecipient = {
                userID: o.id,
                userName: o.name,
                disabled: o.isDisabled,
                //recipients: referenceData.recipients.filter(v => (v.organizationID === model.organizationID || model.setPointID > 0) &&
                recipients: referenceData.recipients.filter(v => 
                                             v.userID === o.id && 
                                            (allowVoiceAlarm || v.type !== RecipientTypeEnum.Voice))
                            .map(v => {

                                const selectedRecipient = model.recipients.find(x => x.recipientID === v.id);

                                const recipient: IFormRecipient = {
                                    id: v.id,
                                    recipientType: v.type,
                                    recipientSubType: v.subType,
                                    to: v.to,
                                    organizationID: v.organizationID,
                                    selected: selectedRecipient !== undefined
                                }

                                return recipient;
                            })
                            .sort((a, b) => a.to.toLowerCase().localeCompare(b.to.toLowerCase()))
            };

            if (userRecipient.recipients.length > 0) {
                formValues.userRecipients.push(userRecipient);
            }
        });

        const messageTemplates = _getMessageTemplates(model, referenceData);

        if (model.setPointID <= 0 && !keepMessaging) {
            formValues.emailAlarmMessageSubject = messageTemplates.emailAlarmMessageSubject;
            formValues.emailAlarmMessageBody = messageTemplates.emailAlarmMessageBody;
            formValues.emailClearMessageSubject = messageTemplates.emailClearMessageSubject;
            formValues.emailClearMessageBody = messageTemplates.emailClearMessageBody;
            formValues.voiceAlarmMessageBody = allowVoiceAlarm ? messageTemplates.emailAlarmMessageBody : messageTemplates.voiceAlarmMessageBody;
            formValues.voiceClearMessageBody = allowVoiceAlarm ? messageTemplates.emailClearMessageBody : messageTemplates.voiceClearMessageBody;
        }

        _updateNotificationProperties(model, formValues);

        return formValues;
    },
    getMessageTemplates: (model: IEditDTO | SetpointFormType, 
        referenceData: IReferenceDTO): SetpointMessageTemplates => {

            return _getMessageTemplates(model, referenceData);
    },
    setpointTagCount: (formData: SetpointFormType): number => {
        return formData.deviceTags.length +
                formData.wellTags.length +
                formData.tankTags.length +
                formData.tankBatteryTags.length;
        
        // return formData.devices.flatMap(o => o.tags).filter(o => o?.selected).length +
        //         formData.wells.flatMap(o => o.tags).filter(o => o?.selected).length +
        //         formData.tanks.flatMap(o => o.tags).filter(o => o?.selected).length +
        //         formData.tankBatteries.flatMap(o => o.tags).filter(o => o?.selected).length;
    },
    evaluationPropertyChanges: (dirtyFields: Partial<Readonly<FieldNamesMarkedBoolean<SetpointFormType>>>): boolean => {


        return (dirtyFields.comparisonType ||
            //dirtyFields.setPointName ||
            dirtyFields.operator ||
            dirtyFields.value ||
            dirtyFields.unitOfMeasureID ||
            dirtyFields.varianceType ||
            dirtyFields.varianceWindow ||
            dirtyFields.varianceTimeframeType ||
            //dirtyFields.devices ||
            //dirtyFields.wells ||
            //dirtyFields.tanks ||
            //dirtyFields.tankBatteries ||
            //dirtyFields.emailAlarmMessageSubject ||
            //dirtyFields.emailAlarmMessageBody ||
            //dirtyFields.voiceAlarmMessageBody ||
            //dirtyFields.voiceClearMessageBody ||
            //dirtyFields.emailClearMessageSubject ||            
            //dirtyFields.emailClearMessageBody ||
            //dirtyFields.alarmColor ||
            //dirtyFields.acknowledgeColor ||
            //dirtyFields.suppressAlarmAlarm ||
            //dirtyFields.suppressNormalNormal ||
            dirtyFields.textValue ||
            dirtyFields.timespanType) ? true : false;
    },

}

export default AlarmSetpointEditorUtils;
